import React from 'react';
import { TextField } from '@mui/material';
import { SentenceApi } from '../api/SentenceApi';

/** Suggestions are drafts. Never replace an existing reading on editor open,
 * or overwrite typing with a late response for this or an older Chinese value. */
export function VerbaScholaPinyinField({ hanzi, value, onChange, chineseOnly = false }: {
    hanzi: string; value: string; chineseOnly?: boolean; onChange: (value: string) => void;
}) {
    const latest = React.useRef({ hanzi, value, onChange });
    latest.current = { hanzi, value, onChange };
    const previousHanzi = React.useRef(hanzi);
    const controller = React.useRef<AbortController | null>(null);
    const [status, setStatus] = React.useState('Suggested pinyin is editable; please review it.');
    React.useEffect(() => {
        const suggestionText = chineseOnly ? (hanzi.match(/[\u4e00-\u9fff]/g) ?? []).join('') : hanzi;
        const changed = previousHanzi.current !== hanzi;
        previousHanzi.current = hanzi;
        if (!suggestionText.trim() || (!changed && latest.current.value.trim())) return;
        const abort = new AbortController();
        controller.current = abort;
        const original = latest.current.value;
        const timer = setTimeout(() => {
            if (abort.signal.aborted) return;
            setStatus('Suggesting pinyin…');
            SentenceApi.getTextPinyin(suggestionText, abort.signal).then(response => {
                if (abort.signal.aborted || latest.current.hanzi !== hanzi || latest.current.value !== original) return;
                latest.current.onChange(response.data.flat().filter(Boolean).join(' '));
                setStatus('Suggested pinyin is editable; please review it.');
            }).catch(() => {
                if (!abort.signal.aborted) setStatus('Could not suggest pinyin. You can enter it manually.');
            });
        }, 300);
        return () => { clearTimeout(timer); abort.abort(); };
    }, [hanzi, chineseOnly]);
    return <TextField label="Pinyin" value={value} helperText={status} fullWidth onChange={event => {
        controller.current?.abort();
        setStatus('Your pinyin will be saved as entered.');
        onChange(event.target.value);
    }} />;
}
